import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCart from '../hooks/useCart';
import { API_BASE } from '../config/api';
import useAuth from '../hooks/useAuth';
import AddressForm from '../components/Checkout/AddressForm.jsx';
import PaymentForm from '../components/Checkout/PaymentForm.jsx';
import OrderConfirmation from './OrderConfirmationPage.jsx';
import '../components/Checkout/CheckoutStyles.css';
import './CheckoutPage.css';
import { bindStorefrontRefresh } from '../utils/storefrontRefresh';

const CheckoutPage = ({ initialStep = 1 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [order, setOrder] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentCompleted, setPaymentCompleted] = useState(initialStep === 3);
  
  const { user, loading: authLoading } = useAuth();
  const { cart, getTotalPrice, clearCart, syncCartProducts } = useCart();
  const navigate = useNavigate();
  
  // Calculate prices
  const itemsPrice = getTotalPrice();
  const shippingPrice = itemsPrice > 1000 ? 0 : 40;
  const taxPrice = 0; // Can be calculated based on your tax rules
  const totalOrderPrice = itemsPrice + shippingPrice + taxPrice;
  const getStockQuantity = (product = {}) => {
    const parsedStockQuantity = Number(product?.stockQuantity);
    return Number.isFinite(parsedStockQuantity) ? Math.max(0, Math.floor(parsedStockQuantity)) : 0;
  };
  const hasInvalidStockItems = (items = []) => items.some((item) => {
    const stockQuantity = getStockQuantity(item.product);
    return stockQuantity <= 0 || (Number(item.quantity) || 0) > stockQuantity;
  });

  // Load order from sessionStorage if we're on the confirmation step
  useEffect(() => {
    if (initialStep === 3 && !order) {
      const savedOrder = sessionStorage.getItem('lastOrder');
      const savedPaymentMethod = sessionStorage.getItem('lastPaymentMethod');
      
      if (savedOrder) {
        setOrder(JSON.parse(savedOrder));
        setPaymentCompleted(true);
      }
      
      if (savedPaymentMethod) {
        setSelectedPaymentMethod(JSON.parse(savedPaymentMethod));
      }
    }
  }, [initialStep, order]);

  useEffect(() => {
    if (user) {
      syncCartProducts();
    }
  }, [user, syncCartProducts]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    return bindStorefrontRefresh(() => {
      syncCartProducts();
    }, { scopes: ['products'] });
  }, [user, syncCartProducts]);
  
  useEffect(() => {
    // Redirect if not logged in or cart is empty
    if (!authLoading && !user) {
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    
    // Only redirect to cart if cart is empty AND we're not on the confirmation step
    // AND payment has not been completed
    // This prevents redirection after payment is completed and cart is cleared
    if (cart.length === 0 && currentStep !== 3 && !order && !paymentCompleted) {
      navigate('/cart');
      return;
    }
  }, [user, authLoading, cart, navigate, currentStep, order, paymentCompleted]);

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
  };
  
  const handlePaymentMethodSelect = async (method) => {
    setSelectedPaymentMethod(method);
    // Once address and online payment are selected, create the order in backend for the payment session.
    if (selectedAddress && method && method.id === 'cashfree') {
      setCreatingOrder(true);
      try {
        const syncedCart = await syncCartProducts();

        if (hasInvalidStockItems(syncedCart)) {
          throw new Error('One or more items in your cart are out of stock or exceed the available quantity.');
        }

        // Prepare order items with correct structure and fallback values
        const orderItems = syncedCart.map(item => ({
          product: item.product._id,
          title: item.product.title || 'Unnamed Product',
          quantity: item.quantity || 1,
          image: item.product.images?.[0] || 'default-product-image.jpg',
          price: item.product.offerPrice || item.product.price || 0,
          selectedSize: item.selectedSize || ''
        }));
        const paymentMethodValue = 'online';
        const orderData = {
          orderItems,
          shippingAddress: selectedAddress,
          paymentMethod: paymentMethodValue,
          itemsPrice,
          shippingPrice,
          taxPrice,
          totalPrice: totalOrderPrice,
          isPaid: false,
          isDelivered: false,
          orderStatus: 'Processing'
        };
        const response = await fetch(`${API_BASE}/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(orderData)
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to create order for payment.');
        }
        const createdOrder = await response.json();
        setOrder(createdOrder);
      } catch (err) {
        await syncCartProducts();
        setError(err.message || 'Could not create order for payment. Please try again.');
      } finally {
        setCreatingOrder(false);
      }
    }
  };
  
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  
  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePaymentFailed = (paymentFailure) => {
    navigate('/payment-failed', {
      state: {
        paymentState: paymentFailure?.paymentState || 'failed',
        message: paymentFailure?.message || ''
      }
    });
  };

  const placeOrder = async (paymentData = null) => {
    setLoading(true);
    setError('');
    console.log('Starting order placement process...');
    
    try {
      const syncedCart = await syncCartProducts();

      // Validate cart items before proceeding
      const invalidItems = syncedCart.filter(item => {
        const stockQuantity = getStockQuantity(item.product);
        // Check if item has product property and all required fields
        return !item.product || !item.product._id || !item.product.title || 
               !item.quantity || !item.product.offerPrice || !item.product.images || 
               item.product.images.length === 0 ||
               stockQuantity <= 0 ||
               item.quantity > stockQuantity ||
               (Array.isArray(item.product.availableSizes) && item.product.availableSizes.length > 0 && !item.selectedSize);
      });
      
      if (invalidItems.length > 0) {
        throw new Error('Some items in your cart are missing required information. Please remove them and try again.');
      }

      // Prepare order items with correct structure and fallback values
      const orderItems = syncedCart.map(item => ({
        product: item.product._id,
        title: item.product.title || 'Unnamed Product',
        quantity: item.quantity || 1,
        image: item.product.images?.[0] || 'default-product-image.jpg',
        price: item.product.offerPrice || item.product.price || 0,
        selectedSize: item.selectedSize || ''
      }));
      
      // Map payment method to backend accepted values
      const paymentMethodValue = 'online';
      
      // Create order object
      const orderData = {
        orderItems,
        shippingAddress: selectedAddress,
        paymentMethod: paymentMethodValue,
        itemsPrice,
        shippingPrice,
        taxPrice,
        totalPrice: totalOrderPrice,
        isPaid: Boolean(paymentData),
        paidAt: paymentData ? new Date() : null,
        isDelivered: false, // Default value
        orderStatus: 'Processing'
      };
      
      // Add payment details based on payment method
      if (selectedPaymentMethod.id === 'razorpay' && paymentData) {
        // For Razorpay payments, use the actual payment data
        orderData.paymentResult = {
          id: paymentData.paymentId,
          status: 'COMPLETED',
          update_time: new Date().toISOString(),
          razorpay_order_id: paymentData.orderId,
          razorpay_signature: paymentData.signature
        };
        console.log('Added Razorpay payment details to order:', orderData.paymentResult);
      } else if (selectedPaymentMethod.id === 'upi' ||
                selectedPaymentMethod.id !== 'razorpay') {
        // For other online payment methods (simulated)
        orderData.paymentResult = {
          id: 'mock_payment_' + Date.now(),
          status: 'COMPLETED',
          update_time: new Date().toISOString(),
          email_address: 'customer@example.com'
        };
        console.log('Added simulated payment details to order:', orderData.paymentResult);
      }
      
      console.log('Sending order data to backend:', JSON.stringify(orderData));
      
      // Send order to backend
      const response = await fetch(`${API_BASE}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Failed to create order';
        
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        if (errorData.errors && errorData.errors.length > 0) {
          errorMessage += ': ' + errorData.errors.join(', ');
        }
        
        if (errorData.details) {
          console.error('Order error details:', errorData.details);
        }
        
        throw new Error(errorMessage);
      }
      
      const createdOrder = await response.json();
      
      // First set the order state and mark payment as completed
      setOrder(createdOrder);
      setPaymentCompleted(true);
      
      // Clear cart after setting order and payment completed flag
      // This ensures the order state is set before the cart is cleared
      clearCart();
      
      // Store order in sessionStorage for access on confirmation page
      sessionStorage.setItem('lastOrder', JSON.stringify(createdOrder));
      sessionStorage.setItem('lastPaymentMethod', JSON.stringify(selectedPaymentMethod));
      
      // Navigate to the new dedicated order confirmation page, passing order and payment method in state
      navigate('/order-confirmation', { state: { order: createdOrder, paymentMethod: selectedPaymentMethod } });
      
    } catch (error) {
      await syncCartProducts();
      console.error('Error creating order:', error);
      setError(error.message || 'An error occurred while placing your order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Process payment based on selected payment method
  const processPayment = (paymentData = null) => {
    console.log('processPayment called with data:', paymentData);
    
    // If we have payment data (from Cashfree payment gateway)
    if (paymentData) {
      console.log('Processing payment with payment data');
      // Set payment completed flag to prevent cart redirection
      setPaymentCompleted(true);
      placeOrder(paymentData);
      return;
    }
  };

  const renderStep = () => {
    console.log('Rendering step:', currentStep, 'Order:', order ? 'exists' : 'null', 'Payment completed:', paymentCompleted);
    switch (currentStep) {
      case 1:
        return (
          <AddressForm 
            onAddressSelect={handleAddressSelect} 
            selectedAddress={selectedAddress} 
            onContinue={handleNextStep} 
          />
        );
      case 2:
        return (
          <PaymentForm 
            onPaymentMethodSelect={handlePaymentMethodSelect} 
            selectedPaymentMethod={selectedPaymentMethod} 
            onContinue={processPayment} 
            onBack={handlePreviousStep} 
            totalAmount={totalOrderPrice}
            itemsPrice={itemsPrice}
            shippingPrice={shippingPrice}
            taxPrice={taxPrice}
            order={order}
            onPaymentFailed={handlePaymentFailed}
          />
        );
      case 3:
        // Ensure we have an order before rendering the confirmation
        if (!order && !loading) {
          console.error('Trying to render confirmation step but order is null');
        }
        return (
          <OrderConfirmation 
            order={order} 
            paymentMethod={selectedPaymentMethod} 
          />
        );
      default:
        return null;
    }
  };
  
  if (loading || creatingOrder) {
    return (
      <div className="checkout-container">
        <div className="loading">Processing your order...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="checkout-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => {
              setError('');
              setLoading(false);
              setCurrentStep(1);
              setSelectedPaymentMethod(null);
              console.log('Try Again button clicked - resetting checkout process');
            }} 
            className="back-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  if (authLoading) {
    return (
      <div className="checkout-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }
  
  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Checkout</h1>
      
      <div className="checkout-steps">
        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-title">Shipping</div>
        </div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-title">Payment</div>
        </div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-title">Confirmation</div>
        </div>
      </div>
      
      <div className={`checkout-content ${currentStep === 2 ? 'checkout-content--payment' : ''}`}>
        {renderStep()}
      </div>
    </div>
  );
};

export default CheckoutPage;
